import type { IdentifierRecord } from '@myplayer/schema';
import { collection, doc, getDoc, getDocs, type Firestore } from 'firebase/firestore';

export const CROSS_ORG_GTIN_CONFLICT_MESSAGE =
  'This GTIN is already registered on MyPlayer. Contact support.';

export interface DuplicateIdentifierConflict {
  readonly identifierKey: string;
  readonly winnerOrgId: string | undefined;
  readonly winnerWorkspaceId: string | undefined;
  readonly winnerWorkspaceName: string;
  readonly message: string;
}

export function buildSameOrgGtinConflictMessage(workspaceName: string): string {
  return `This GTIN is already registered in workspace "${workspaceName}".`;
}

export function parseRecordCreatedAtMs(record: IdentifierRecord): number {
  const rawCreatedAt = (record as { readonly createdAt?: unknown }).createdAt;
  if (typeof rawCreatedAt === 'string' && rawCreatedAt.trim().length > 0) {
    const parsed = Date.parse(rawCreatedAt.trim());
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  if (rawCreatedAt !== null && typeof rawCreatedAt === 'object') {
    const maybeToDate = (rawCreatedAt as { readonly toDate?: unknown }).toDate;
    if (typeof maybeToDate === 'function') {
      const dateVal = (maybeToDate as () => Date).call(rawCreatedAt);
      if (dateVal instanceof Date && !Number.isNaN(dateVal.getTime())) {
        return dateVal.getTime();
      }
    }
    const maybeSeconds = (rawCreatedAt as { readonly seconds?: unknown }).seconds;
    const maybeNanos = (rawCreatedAt as { readonly nanoseconds?: unknown }).nanoseconds;
    if (typeof maybeSeconds === 'number' && Number.isFinite(maybeSeconds)) {
      const nanosMs =
        typeof maybeNanos === 'number' && Number.isFinite(maybeNanos)
          ? Math.floor(maybeNanos / 1_000_000)
          : 0;
      return maybeSeconds * 1000 + nanosMs;
    }
  }
  return Number.MAX_SAFE_INTEGER;
}

function isRecordArchived(record: IdentifierRecord): boolean {
  return record.status === 'ARCHIVED' || record.draft?.status === 'ARCHIVED';
}

export function buildRecordConflictKey(record: {
  readonly orgId?: string | undefined;
  readonly workspaceId?: string | undefined;
  readonly identifierKey: string;
}): string {
  return `${record.orgId ?? ''}::${record.workspaceId ?? ''}::${record.identifierKey}`;
}

/**
 * Deterministically identifies duplicate non-archived identifiers across workspaces/orgs.
 * Oldest `createdAt` wins; all newer records sharing the same `identifierKey` are returned as conflicts.
 */
export function computeDuplicateIdentifierConflicts(
  records: readonly IdentifierRecord[],
  workspaceNameMap?: ReadonlyMap<string, string>,
): ReadonlyMap<string, DuplicateIdentifierConflict> {
  const uniqueByPath = new Map<string, IdentifierRecord>();
  for (const rec of records) {
    if (isRecordArchived(rec)) continue;
    uniqueByPath.set(buildRecordConflictKey(rec), rec);
  }

  const byIdentifierKey = new Map<string, IdentifierRecord[]>();
  for (const rec of uniqueByPath.values()) {
    const list = byIdentifierKey.get(rec.identifierKey);
    if (list) {
      list.push(rec);
    } else {
      byIdentifierKey.set(rec.identifierKey, [rec]);
    }
  }

  const conflicts = new Map<string, DuplicateIdentifierConflict>();
  for (const [identifierKey, group] of byIdentifierKey.entries()) {
    if (group.length <= 1) continue;
    const sorted = group.slice().sort((a, b) => {
      const timeDiff = parseRecordCreatedAtMs(a) - parseRecordCreatedAtMs(b);
      if (timeDiff !== 0) return timeDiff;
      const orgCmp = (a.orgId ?? '').localeCompare(b.orgId ?? '');
      if (orgCmp !== 0) return orgCmp;
      return (a.workspaceId ?? '').localeCompare(b.workspaceId ?? '');
    });
    const winner = sorted[0];
    if (!winner) continue;
    const winnerWorkspaceName =
      (winner.workspaceId ? workspaceNameMap?.get(winner.workspaceId) : undefined) ??
      winner.workspaceId ??
      'another workspace';

    for (const loser of sorted.slice(1)) {
      const isCrossOrg =
        winner.orgId !== undefined &&
        loser.orgId !== undefined &&
        winner.orgId.length > 0 &&
        loser.orgId.length > 0 &&
        winner.orgId !== loser.orgId;
      const message = isCrossOrg
        ? CROSS_ORG_GTIN_CONFLICT_MESSAGE
        : buildSameOrgGtinConflictMessage(winnerWorkspaceName);
      conflicts.set(buildRecordConflictKey(loser), {
        identifierKey,
        winnerOrgId: winner.orgId,
        winnerWorkspaceId: winner.workspaceId,
        winnerWorkspaceName,
        message,
      });
    }
  }

  return conflicts;
}

export function getDuplicateConflictForRecord(
  record: IdentifierRecord,
  conflicts?: ReadonlyMap<string, DuplicateIdentifierConflict>,
): DuplicateIdentifierConflict | undefined {
  if (!conflicts || conflicts.size === 0) return undefined;
  return conflicts.get(buildRecordConflictKey(record));
}

export function checkAdminIdentifierUniqueness(params: {
  readonly identifierKey: string;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly existingRecords?: readonly IdentifierRecord[] | undefined;
  readonly workspaceNameMap?: ReadonlyMap<string, string> | undefined;
  readonly allowSameWorkspaceUpdate?: boolean | undefined;
}): string | null {
  const existingRecords = params.existingRecords ?? [];
  const matching = existingRecords
    .filter((rec) => {
      if (rec.identifierKey !== params.identifierKey) return false;
      if (isRecordArchived(rec)) return false;
      if (
        params.allowSameWorkspaceUpdate === true &&
        (rec.orgId ?? params.orgId) === params.orgId &&
        (rec.workspaceId ?? params.workspaceId) === params.workspaceId
      ) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      const timeDiff = parseRecordCreatedAtMs(a) - parseRecordCreatedAtMs(b);
      if (timeDiff !== 0) return timeDiff;
      const orgCmp = (a.orgId ?? '').localeCompare(b.orgId ?? '');
      if (orgCmp !== 0) return orgCmp;
      return (a.workspaceId ?? '').localeCompare(b.workspaceId ?? '');
    });

  const conflict = matching[0];
  if (!conflict) return null;

  if (conflict.orgId && conflict.orgId.length > 0 && conflict.orgId !== params.orgId) {
    return CROSS_ORG_GTIN_CONFLICT_MESSAGE;
  }

  const wsName =
    (conflict.workspaceId ? params.workspaceNameMap?.get(conflict.workspaceId) : undefined) ??
    conflict.workspaceId ??
    params.workspaceId;
  return buildSameOrgGtinConflictMessage(wsName);
}

export async function checkFirestoreOrgIdentifierUniqueness(params: {
  readonly db: Firestore;
  readonly identifierKey: string;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly knownWorkspaceIds?: readonly string[] | undefined;
  readonly workspaceNameMap?: ReadonlyMap<string, string> | undefined;
}): Promise<string | null> {
  const candidateWsIds = new Set<string>(
    [params.workspaceId, ...(params.knownWorkspaceIds ?? [])]
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
  );
  const discoveredNames = new Map<string, string>();

  try {
    const wsColSnap = await getDocs(
      collection(params.db, 'organisations', params.orgId, 'workspaces'),
    );
    for (const wsDoc of wsColSnap.docs) {
      candidateWsIds.add(wsDoc.id);
      const data = wsDoc.data() as Record<string, unknown> | undefined;
      if (typeof data?.['name'] === 'string' && data['name'].trim().length > 0) {
        discoveredNames.set(wsDoc.id, data['name'].trim());
      }
    }
  } catch {
    // Non-ORG_ADMIN roles may not have collection list permission on /workspaces; candidateWsIds still covers accessible workspaces.
  }

  for (const wsId of candidateWsIds) {
    try {
      const idRef = doc(
        params.db,
        'organisations',
        params.orgId,
        'workspaces',
        wsId,
        'identifiers',
        params.identifierKey,
      );
      const snap = await getDoc(idRef);
      if (snap.exists()) {
        const data = snap.data() as Record<string, unknown> | undefined;
        if (data?.['status'] !== 'ARCHIVED') {
          const wsName = params.workspaceNameMap?.get(wsId) ?? discoveredNames.get(wsId) ?? wsId;
          return buildSameOrgGtinConflictMessage(wsName);
        }
      }
    } catch {
      // Skip workspaces where caller lacks read permission
    }
  }

  return null;
}
